import { ComponentProps, type MouseEvent, useContext, useEffect } from 'react';

import { Checkbox, Flex, HStack, Text } from '@chakra-ui/react';

import { ProductCatalogV2, ProductSlug } from '../../../../gql/generated/graphql';

import Card from '../../../shared/Card';

import ProductSelector from './ProductSelector';
import Reserve from './Reserve';

import { PermissionEnum } from '../../../../constants/permissions';
import { DealContext } from '../../../../libs/DealContext';
import { AbilityContext } from '../../../../libs/contextLib';
import { formatMoney } from '../../../../libs/utils';
import { PackageConfig, ProductAvailability } from '../../../../types/products';
import { DebouncedSave } from '../../DealInfoBuyoutForm';
import { paymentOptionsTypes } from '../../types';

type PackageBaseProps = Omit<ComponentProps<typeof Card>, 'onClick'> & {
  name: string;
  option: paymentOptionsTypes;
  isSelected: boolean;
  isDisabled?: boolean;
  isAvailable?: boolean;
  onClick?: () => void;
  // New props for dynamic product selection
  packageConfig?: PackageConfig;
  products?: ProductCatalogV2[];
  onToggleProduct?: (slug: ProductSlug) => void;
  getProductAvailability?: (slug: ProductSlug) => ProductAvailability;
};
type PackageConditionalProps = {
  showReserveEdit: boolean;
  debounceSave?: DebouncedSave;
};
type PackageProps = PackageBaseProps & PackageConditionalProps;

const Package = ({
  name,
  option,
  isSelected,
  isDisabled,
  isAvailable,
  onClick,
  showReserveEdit,
  debounceSave,
  packageConfig,
  products,
  onToggleProduct,
  getProductAvailability,
  ...rest
}: PackageProps) => {
  const { deal } = useContext(DealContext);
  const abilities = useContext(AbilityContext);
  const canUseAiCloser = abilities.has(PermissionEnum.UseAiCloser);

  const showProductSelector =
    packageConfig && products && products.length > 0 && onToggleProduct && getProductAvailability;

  // Add effect to handle highlight class
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (isSelected && canUseAiCloser) {
      const element = document.querySelector(
        `[data-option-slot="${packageConfig?.slotIndex}"]`,
      ) as HTMLElement | null;
      if (element) {
        element.classList.add('field-highlight');
        timer = setTimeout(() => element.classList.remove('field-highlight'), 900);
      }
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [canUseAiCloser, isSelected, packageConfig?.slotIndex]);

  const darkestAvailableColor = 'queenBlue';
  const getDarkestColor = () => {
    if (isAvailable) {
      return isDisabled ? 'queenBlueLight' : darkestAvailableColor;
    }
    return isDisabled ? 'gray.500' : 'gray.600';
  };
  const darkestColor = getDarkestColor();

  // Add docFee to option.profit when necessary
  const displayedProfit = (deal?.financial_info?.doc_fee ?? 0) + option.profit;

  // Handle card click - only trigger if clicking outside the selector
  const handleCardClick = (e: MouseEvent) => {
    // Don't trigger selection if clicking on the product selector
    const target = e.target as HTMLElement;
    if (target.closest('[data-product-selector]')) {
      return;
    }

    if (!onClick || !isAvailable || isDisabled) {
      return;
    }

    onClick();
  };

  return (
    <Card
      data-option-name={option.name}
      data-option-slot={packageConfig?.slotIndex}
      variant="roundedWithBorder"
      w="165px"
      minW="140px"
      h="150px"
      color={darkestColor}
      bgColor={isSelected ? 'azureishWhite' : isAvailable ? 'white' : 'backgroundGray'}
      borderColor={darkestColor}
      fontSize="sm"
      cursor={!isAvailable || isDisabled ? 'not-allowed' : 'pointer'}
      onClick={handleCardClick}
      {...rest}
    >
      <HStack
        minH="37px"
        borderBottom="1px"
        borderBottomColor={isSelected ? 'white' : isAvailable ? 'azureishWhite' : 'gray.400'}
        justify="space-between"
        px={2}
      >
        {showProductSelector ? (
          <Flex data-product-selector onClick={(e) => e.stopPropagation()}>
            <ProductSelector
              products={products}
              selectedSlugs={packageConfig.selectedProductSlugs}
              onToggle={onToggleProduct}
              getAvailability={getProductAvailability}
              isDisabled={isDisabled}
            />
          </Flex>
        ) : (
          <Text px={1} py={2} whiteSpace="nowrap">
            {name}
          </Text>
        )}
      </HStack>
      <HStack
        minH="40px"
        justify="space-between"
        py={1}
        px={
          abilities.has(PermissionEnum.SeeCommission) && abilities.has(PermissionEnum.SeeProfit)
            ? 1
            : 2
        }
      >
        <Text fontWeight="normal">
          {abilities.has(PermissionEnum.SeeCommission) ? formatMoney(option.closerCommission) : ''}
        </Text>
        {showReserveEdit && debounceSave ? (
          <Flex color={darkestAvailableColor}>
            {/* Only shown in the No Protection/No Products package */}
            <Reserve
              debouncedSave={debounceSave}
              noProductOption={{ ...option, profit: displayedProfit }}
            />
          </Flex>
        ) : abilities.has(PermissionEnum.SeeProfit) ? (
          <Text fontWeight="normal">{formatMoney(displayedProfit)}</Text>
        ) : null}
      </HStack>
      <HStack minH="45px" justify="center">
        <Text
          fontSize={{
            base: 'lg',
            '2xl': 'xl',
            '3xl': '2xl',
            '4xl': option.sellPayment >= 100000 ? '2xl' : '3xl',
          }}
          fontWeight="normal"
          px={3}
        >
          {formatMoney(option.sellPayment)}
        </Text>
      </HStack>
      <HStack h="26px" bgColor={darkestColor} color="white" borderBottomRadius="8px">
        {isAvailable ? (
          <Checkbox size="md" px={3} isChecked={isSelected} isDisabled={isDisabled}>
            <Text fontSize="xs">{isSelected ? 'SELECTED' : 'SELECT'}</Text>
          </Checkbox>
        ) : (
          <Text pt={2} fontSize="xs" px={3} mt={-2}>
            UNAVAILABLE
          </Text>
        )}
      </HStack>
    </Card>
  );
};

export default Package;
